/**
 * Shared one-truth predicate for "is this a `geometry/<rest>` command that edits
 * a dynamic-BLOCK-DEFINITION authoring structure" (parameters / actions /
 * visibility states / lookup tables / chain groups / block-property tables, plus
 * the BV* visibility-state visibility toggles).
 *
 * Extracted so the two domain consumers share ONE source of truth (CLAUDE.md
 * Этап-1 dup gate — DEFAULT = eliminate the clone):
 *   - `contracts/auditTaxonomy.ts` `categorizeGeometry` (the audit `block`
 *     category — it ADDITIONALLY treats `*insert*` as block; see note below).
 *   - `drawing-model/revisionModel.ts` `classifyGeometryRest` (the ABB-S1 bump
 *     `table` class — it routes per-INSTANCE `*insert*` overrides to GEOMETRY, so
 *     it does NOT fold `insert` into this predicate).
 *
 * This module lives in `drawing-model` (the lower package) precisely so
 * `contracts` — which DEPENDS ON `drawing-model` — can import it WITHOUT creating
 * a `contracts ↔ drawing-model` cycle (ПРИОРИТЕТ #2). The `insert` keyword is
 * deliberately NOT part of the shared predicate: it straddles the two consumers'
 * boundaries, so each layers its own `*insert*` handling on top.
 */

/**
 * Keyword substrings of a `geometry/<rest>` literal that mark a dynamic-block-
 * DEFINITION authoring edit. Excludes `insert` (a per-instance concept handled
 * by each caller) and the `bv` prefix (checked separately below).
 */
const DYNAMIC_BLOCK_AUTHORING_KEYWORDS: readonly string[] = [
  "block",
  "visibility",
  "lookup",
  "chain",
  "dynamic",
  "parameter",
  "action",
  "property",
] as const;

/**
 * True when `rest` (the part of a `geometry/<rest>` command after the slash) is a
 * dynamic-block-DEFINITION authoring edit: a `bv*` visibility toggle OR a rest
 * containing any {@link DYNAMIC_BLOCK_AUTHORING_KEYWORDS} substring. Does NOT
 * match per-instance `*insert*` overrides — callers handle those explicitly.
 */
export function isDynamicBlockAuthoringRest(rest: string): boolean {
  if (rest.startsWith("bv")) return true; // bvhide / bvshow / bvshow-all
  return DYNAMIC_BLOCK_AUTHORING_KEYWORDS.some((keyword) =>
    rest.includes(keyword),
  );
}
